const post = {
  slug: "instagram-dm-reply-templates-for-sellers",
  title: "Instagram DM Reply Templates for Sellers: 15 Messages You Can Copy",
  seoTitle: "Instagram DM Reply Templates for Sellers",
  description:
    "15 copy-ready Instagram DM reply templates for sellers: price questions, payment, shipping delays, returns and follow-ups, plus how to keep them sounding human.",
  excerpt:
    "Most Instagram sales are won or lost in the first few replies. Here are 15 short templates for the messages sellers send every day, and how to make them sound like you.",
  category: "Selling on Instagram",
  tags: ["Instagram DM templates", "Instagram seller replies", "Customer messages for sellers"],
  date: "2026-10-22",
  cover: {
    src: "/blog-images/instagram-dm-reply-templates-for-sellers-cover.png",
    alt: "A phone showing a short, friendly seller reply in an Instagram DM",
  },
  blocks: [
    {
      type: "p",
      text:
        "If you sell on Instagram, you type the same messages again and again. \u201CPrice?\u201D, \u201CIs it available?\u201D, \u201CWhen will it reach me?\u201D Each one is a small chance to win or lose a sale, and a slow or unclear reply is the easiest way to lose it.",
    },
    {
      type: "p",
      text:
        "Below are 15 templates for the moments that come up most. Copy them, change the words in brackets and adjust the tone until they sound like you. They are starting points, not scripts to repeat word for word.",
    },

    { type: "h2", text: "How to use these templates well" },
    {
      type: "ul",
      items: [
        "**Use the customer\u2019s name.** One personal word is the difference between a reply and a robot.",
        "**Answer first, ask second.** Give the price or the answer, then ask one clear question to move things forward.",
        "**Keep it short.** Most people read DMs on a phone while doing something else. Two or three lines is enough.",
        "**Write in your own voice.** If you normally use emoji and casual words, keep doing it. Customers trust a voice that matches your posts.",
        "**Reply quickly.** A fast, simple answer beats a perfect answer that arrives tomorrow.",
      ],
    },

    { type: "h2", text: "First replies" },
    {
      type: "h3",
      text: "1. Price enquiry",
    },
    {
      type: "p",
      text:
        "\u201CHi [name]! The [product] is \u20B9[price]. It comes in [sizes or colours]. Which one would you like?\u201D",
    },
    { type: "h3", text: "2. Availability question" },
    {
      type: "p",
      text:
        "\u201CYes, the [product] is in stock in [size]. Shall I reserve it for you?\u201D",
    },
    { type: "h3", text: "3. Sold out" },
    {
      type: "p",
      text:
        "\u201CThat size is sold out right now. I can offer [alternative], or message you as soon as it is back. What would you prefer?\u201D",
    },
    { type: "h3", text: "4. Help choosing a size" },
    {
      type: "p",
      text:
        "\u201CHappy to help you choose. What is your height and your usual size? I will tell you which one fits best.\u201D",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "A quick first reply",
      caption: "Illustrative example. Use your own products and wording.",
      messages: [
        { from: "buyer", text: "Price?" },
        { from: "shop", text: "Hi Riya! The linen shirt is \u20B91,299. It comes in S, M and L. Which size would you like?" },
        { from: "buyer", text: "M please" },
        { from: "shop", text: "Great, M is in stock. Shall I send the payment link?" },
      ],
    },

    { type: "h2", text: "Taking payment" },
    { type: "h3", text: "5. Order summary and payment link" },
    {
      type: "p",
      text:
        "\u201CYour order: [product], size [size], \u20B9[total], delivered to [city]. Here is your payment link: [link]. I will confirm as soon as it goes through.\u201D",
    },
    { type: "h3", text: "6. Payment screenshot received" },
    {
      type: "p",
      text:
        "\u201CThank you, I have your screenshot. I will confirm your order as soon as the payment shows in my account.\u201D Our guide to [avoiding fake payment screenshots](/blog/avoid-fake-payment-screenshots-instagram-sellers) explains why this wait matters.",
    },
    { type: "h3", text: "7. Asking for a discount" },
    {
      type: "p",
      text:
        "\u201CMy prices are fixed so I can keep the quality the same, but you get [free delivery or a coupon] on orders above \u20B9[amount].\u201D",
    },

    { type: "h2", text: "After the order" },
    { type: "h3", text: "8. Order confirmed" },
    {
      type: "p",
      text:
        "\u201CPayment received, thank you! Your order [number] is confirmed and will be shipped within [X] days.\u201D",
    },
    { type: "h3", text: "9. Order shipped" },
    {
      type: "p",
      text:
        "\u201CYour order is on its way! Tracking: [link]. It should reach you in [date range].\u201D",
    },
    { type: "h3", text: "10. A delay" },
    {
      type: "p",
      text:
        "\u201CI am sorry, your order is delayed because [reason]. The new dispatch date is [date]. Thank you for your patience, and tell me if you would rather cancel.\u201D",
    },

    { type: "h2", text: "Problems and follow-ups" },
    { type: "h3", text: "11. A return request" },
    {
      type: "p",
      text:
        "\u201CSorry it did not work out. Please send a photo of the item and your order number, and I will share the return steps.\u201D",
    },
    { type: "h3", text: "12. Wrong or damaged item" },
    {
      type: "p",
      text:
        "\u201CI am really sorry about this. Please send a photo of the item and the parcel, and I will sort it out right away.\u201D",
    },
    { type: "h3", text: "13. A gentle nudge" },
    {
      type: "p",
      text:
        "\u201CHi [name], your [product] is still waiting. Want me to hold it for you? Reply YES and I will send the payment link.\u201D See [abandoned carts in DMs](/blog/abandoned-carts-instagram-dms) for more on timing these nudges.",
    },
    { type: "h3", text: "14. Asking for a review" },
    {
      type: "p",
      text:
        "\u201CHope you love it! If you have a minute, a short review or a photo would mean a lot to a small shop like mine.\u201D",
    },
    { type: "h3", text: "15. A bulk or custom order" },
    {
      type: "p",
      text:
        "\u201CThanks for asking! For bulk or custom orders, please share the product, quantity and delivery city, and I will send you a quote today.\u201D",
    },

    { type: "h2", text: "Keep templates sounding human" },
    {
      type: "p",
      text:
        "Templates save time, but customers can tell when a message was pasted without thought. Three small habits keep them warm.",
    },
    {
      type: "ul",
      items: [
        "**Change one detail every time.** Mention the product, the city or something the customer said. It takes seconds.",
        "**Read it out loud.** If you would not say it to a customer standing in front of you, rewrite it.",
        "**Retire stale lines.** If a template sounds the same as it did six months ago, refresh it.",
      ],
    },

    { type: "h2", text: "What to automate and what to write yourself" },
    {
      type: "p",
      text:
        "Many of these messages are the same for every buyer, which makes them good candidates for automation. In Checkout DM, showing the catalog, collecting the size and delivery address, sending a payment link and starting a return request all happen inside the chat, so you do not type them at all. Checkout DM has no reply templates: these automated messages use fixed wording, the same for every merchant and customer, and you cannot change it. That is why the templates above are for the messages you write yourself, the personal replies that automation does not cover. Plans from Starter upward also send delivery status updates, and Growth and Pro add abandoned cart recovery. Our guide to [selling on Instagram DMs without losing orders](/blog/sell-on-instagram-dms-without-losing-orders) shows how the pieces fit together.",
    },
    {
      type: "callout",
      tone: "mint",
      title: "Keep the personal ones for yourself",
      text:
        "Automate the repetitive steps, and write the human moments yourself: an apology for a delay, a reply to a worried buyer or a thank-you to a loyal customer. Those are the messages people remember.",
    },
    {
      type: "p",
      text:
        "Whichever way you work, test your replies from a second Instagram account before you rely on them, and update them whenever customers keep asking the same follow-up question. A good template is one that answers that question before it is asked.",
    },
  ],
  related: [
    { to: "/tabs-guide", title: "Dashboard Tabs Guide", description: "Chats, Orders, Automations and the rest of your dashboard." },
    { to: "/catalog-guide", title: "Catalog Guide", description: "Set up products, sizes and variants for your chats." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on plans, payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "What should I reply when someone asks for the price on Instagram?",
      a: "Give the price straight away, mention the available sizes or options, and ask one clear question, such as which size they want. A direct answer with a next step converts better than asking them to check your bio.",
    },
    {
      q: "Is it okay to use the same template for every customer?",
      a: "Yes, as long as you personalise one detail, such as the name or the product. Templates save time, but a message that sounds pasted can cost you the sale.",
    },
    {
      q: "How fast should I reply to Instagram DMs?",
      a: "As quickly as you reasonably can. People who message a shop usually want an answer within minutes, so a short, fast reply is better than a long one that arrives later.",
    },
    {
      q: "Can Checkout DM send these replies for me?",
      a: "No. Checkout DM does not use reply templates, and its automated messages have fixed wording that is the same for every merchant and customer. It handles the repetitive selling steps inside the chat, including the catalog, size choice, delivery address, payment link and return requests. Use the templates in this article for the replies you write yourself, and you can still reply personally whenever a customer needs your attention.",
    },
  ],
};

export default post;